<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-account-circle</v-icon>
        User Details
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
          <v-btn
            color="#002060"
            dark
            rounded
            size="large"
            @click="editUser"
            class="mr-2"
          >
            <v-icon left>mdi-pencil</v-icon>
            Edit User
          </v-btn>
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>

        <!-- User Header -->
        <div class="text-center mb-6">
          <v-avatar size="80" color="#002060" class="mb-3">
            <v-icon size="40" color="white">mdi-account-circle</v-icon>
          </v-avatar>
          <h2 class="user-name mb-2">{{ user.firstName }} {{ user.surname }}</h2>
          <p class="user-email mb-3">{{ user.email }}</p>
          <v-chip 
            :color="getRoleColor(user.role)" 
            size="large"
            class="role-chip"
          >
            {{ user.role }}
          </v-chip>
        </div>

        <!-- User Details Sections -->
        <v-row>
          <!-- Personal Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Personal Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.firstName || 'N/A'"
              label="First Name"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.surname || 'N/A'"
              label="Surname"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.email || 'N/A'"
              label="Email"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-email"
              readonly
            />
          </v-col>
          
                      <v-col cols="12" md="6">
              <v-text-field
                :model-value="formatUserPhone(user.phone)"
                label="Phone"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                readonly
              />
            </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.employeeId || 'N/A'"
              label="Employee ID"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-card-account-details"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- Role & Permissions -->
          <v-col cols="12">
            <h3 class="section-title mb-4">Role & Permissions</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.role || 'N/A'"
              label="User Role"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-shield-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="getPermissions(user.role)"
              label="Permissions"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-key"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.department || 'Legal'"
              label="Department"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-office-building"
              readonly
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- System Information -->
          <v-col cols="12">
            <h3 class="section-title mb-4">System Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.dateAdded || 'N/A'"
              label="Date Added"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-calendar"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="user.lastLogin || 'Never'"
              label="Last Login"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-clock"
              readonly
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneNumber } from '../utils/phoneFormatter';

export default {
  name: 'ViewUser',
  components: {
    NotificationDialog
  },
  data() {
    return {
      user: {},
      loading: true,
      error: null,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: ''
    };
  },
  methods: {
    formatUserPhone(phone) {
      return formatPhoneNumber(phone) || 'N/A';
    },

    getStatusColor(status) {
      const colors = {
        active: 'success',
        inactive: 'error',
        suspended: 'warning'
      };
      return colors[status] || 'success';
    },

    getStatusLabel(status) {
      const labels = {
        active: 'Active',
        inactive: 'Deactivated',
        suspended: 'Suspended'
      };
      return labels[status] || 'Active';
    },

    async loadUser() {
      try {
        this.loading = true;
        this.error = null;
        
        const userId = this.$route.params.id;
        if (!userId) {
          this.error = 'No user ID provided';
          return;
        }

        // Try to load from Firestore first - using 'users' collection
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            this.user = { id: userId, ...userDoc.data() };
          } else {
            // Fall back to mock data
            this.loadMockUser(userId);
          }
        } catch (dbError) {
          console.warn('Firestore error, using mock data:', dbError);
          this.loadMockUser(userId);
        }
      } catch (err) {
        console.error('Error loading user:', err);
        this.error = 'Failed to load user details';
      } finally {
        this.loading = false;
      }
    },
    
    loadMockUser(userId) {
      // Mock data for demo purposes - matching User.vue data structure
      const mockUsers = [
        {
          id: 'user1',
          firstName: 'Michiel',
          surname: 'Mostert',
          employeeId: 'EMP001',
          role: 'admin',
          email: 'mostert@example.com',
          status: 'active',
          dateAdded: '06/18/2025',
          hasTemporaryPassword: true,
          temporaryPassword: 'AB1234cd'
        },
        {
          id: 'user2',
          firstName: 'Jane',
          surname: 'Doe',
          employeeId: 'EMP002',
          role: 'manager',
          email: 'jane.doe@example.com',
          status: 'active',
          dateAdded: '06/17/2025',
          hasTemporaryPassword: false
        },
        {
          id: 'user3',
          firstName: 'John',
          surname: 'Smith',
          employeeId: 'EMP003',
          role: 'user',
          email: 'john.smith@example.com',
          status: 'active',
          dateAdded: '06/16/2025',
          hasTemporaryPassword: true,
          temporaryPassword: 'CD5678ef'
        }
      ];
      
      this.user = mockUsers.find(u => u.id === userId) || mockUsers[0];
    },
    
    getRoleColor(role) {
      const colors = {
        admin: 'red',
        Executive: 'purple',
        Employee: 'blue',
        'QA Agent': 'green'
      };
      return colors[role] || 'green';
    },
    
    getPermissions(role) {
      switch (role) {
        case 'admin': return 'Full System Access';
        case 'Executive': return 'Full System Access';
        case 'Employee': return 'Client & QA Access';
        case 'QA Agent': return 'QA Access Only';
        default: return 'No Permissions';
      }
    },
    
    editUser() {
      console.log('Edit user:', this.user.id);
      this.$router.push(`/edit-user/${this.user.id}`);
    }
  },
  async created() {
    await this.loadUser();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.user-name {
  font-size: 1.75rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.user-email {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.role-chip {
  font-weight: 600;
  font-size: 1rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 